import { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './CollapsibleSQL.css';

interface CollapsibleSQLProps {
  sql: string;
  description?: string;
}

function CollapsibleSQL({ sql, description }: CollapsibleSQLProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Don't toggle expand/collapse
    try {
      await navigator.clipboard.writeText(sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy SQL:', err);
    }
  };

  return (
    <div className="collapsible-sql">
      <button
        className="collapsible-sql-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="collapsible-sql-icon">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        <span className="collapsible-sql-emoji">📝</span>
        <span className="collapsible-sql-label">
          SQL Generated
          {description && <span className="collapsible-sql-description">: {description}</span>}
        </span>
        <span className="collapsible-sql-hint">
          {isExpanded ? 'Click to collapse' : 'Click to expand'}
        </span>
      </button>
      {isExpanded && (
        <div className="collapsible-sql-content">
          <div className="collapsible-sql-toolbar">
            <span className="sql-language-badge">SQL</span>
            <button
              className={`sql-copy-button ${copied ? 'copied' : ''}`}
              onClick={handleCopy}
              aria-label={copied ? 'Copied!' : 'Copy SQL'}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
          <SyntaxHighlighter
            language="sql"
            style={oneDark}
            customStyle={{
              margin: 0,
              borderRadius: '0 0 6px 6px',
              fontSize: '0.875rem',
              padding: '1em',
            }}
            wrapLongLines
          >
            {sql}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
}

export default CollapsibleSQL;
